/**
 * Catalog Normalizer
 * Transforms catalog modules API responses to frontend models.
 * @module api/normalizers/catalog
 */

// LLM:METADATA
// :hierarchy: [Domain:Catalog | Data_Normalization | Service]
// :relates-to: uses: "catalog contract schemas", required-by: "catalog.client.js"
// :rationale: "Transforms backend catalog responses (modules, fields, items) to match frontend expectations with consistent field naming (camelCase), null safety, and proper array structure handling"
// :contract: pre: "validated data from catalog contract", post: "normalized catalog response for Catalog modules/components", invariant: "always returns expected array/object structure with camelCase fields"
// :decision_cache: "Chose simple transformation normalizer for catalog modules since backend structure closely matches frontend needs. Key transformations: snake_case to camelCase, null safety for optional fields, array filtering for integrity [CATALOG-NORM-001]"
// LLM:END

/**
 * Normalize catalog module from API response
 * Transforms backend module structure to frontend model
 *
 * @param {Object} module - Raw module data from API
 * @param {string} module.id - Module ID (string from PHP mysqli)
 * @param {string} [module.name] - Module name
 * @param {string} [module.table_name] - Table name
 * @param {number} [module.active] - Active status (0/1)
 * @param {string} [module.created_at] - Creation timestamp
 * @param {string} [module.updated_at] - Update timestamp
 * @returns {Object} Normalized module object
 * @returns {number} returns.id - Module ID (converted to number)
 * @returns {string} [returns.name] - Module name
 * @returns {string} [returns.tableName] - Table name
 * @returns {number} [returns.active] - Active status
 * @returns {string} [returns.createdAt] - Creation timestamp
 * @returns {string} [returns.updatedAt] - Update timestamp
 */
export function normalizeModule(module) {
  if (!module || typeof module !== "object") {
    return null
  }

  return {
    id: module.id != null ? Number(module.id) : null,
    name: module.name ?? null,
    tableName: module.table_name ?? null,
    active: module.active ?? null,
    createdAt: module.created_at ?? null,
    updatedAt: module.updated_at ?? null,
  }
}

/**
 * Normalize modules list response
 * Transforms backend modules array to frontend model structure
 *
 * @param {Array} modules - Raw modules array
 * @returns {Array} Normalized modules array
 */
export function normalizeModulesList(modules) {
  if (!Array.isArray(modules)) {
    return []
  }

  return modules.map(normalizeModule).filter(Boolean)
}

/**
 * Normalize catalog module field from API response
 * Transforms backend field structure to frontend model
 *
 * @param {Object} field - Raw field data from API
 * @param {string} field.id - Field ID (string from PHP mysqli)
 * @param {string} field.name - Field name
 * @param {string} field.type - Field type
 * @param {string} [field.label] - Field label
 * @param {boolean} [field.required] - Required flag
 * @param {*} [field.default] - Default value
 * @param {*} [field.options] - Field options
 * @returns {Object} Normalized field object
 * @returns {number} returns.id - Field ID (converted to number)
 * @returns {string} returns.name - Field name
 * @returns {string} returns.type - Field type
 * @returns {string} [returns.label] - Field label
 * @returns {boolean} [returns.required] - Required flag
 * @returns {*} [returns.default] - Default value
 * @returns {*} [returns.options] - Field options
 */
export function normalizeModuleField(field) {
  if (!field || typeof field !== "object") {
    return null
  }

  return {
    id: field.id != null ? Number(field.id) : null,
    name: field.name ?? "",
    type: field.type ?? "",
    label: field.label ?? null,
    required: field.required ?? null,
    default: field.default ?? null,
    options: field.options ?? null,
    counter: field.counter ?? null,
  }
}

/**
 * Normalize module fields response
 * Transforms backend fields array to frontend model structure
 *
 * @param {Array} fields - Raw fields array
 * @returns {Array} Normalized fields array
 */
export function normalizeModuleFields(fields) {
  if (!Array.isArray(fields)) {
    return []
  }

  return fields.map(normalizeModuleField).filter(Boolean)
}

/**
 * Normalize module field metadata used by filter consumers.
 * Owns backend-to-UI fallbacks so page-level composables receive one canonical shape.
 */
export function normalizeModuleFilterFields(fields, module = {}) {
  const normalized = Array.isArray(fields)
    ? fields
        .filter((field) => field && typeof field === "object")
        .map((field) => ({ ...field }))
    : []

  const isLpCd =
    [112, 115].includes(Number(module.moduleId)) ||
    ["lp", "cd"].includes(module.moduleName)
  if (!isLpCd) return normalized

  const withCountryLink = normalized.map((field) =>
    field.name === "country" && !field.module_link_name
      ? { ...field, module_link_name: "countries" }
      : field
  )
  if (withCountryLink.some((field) => field.name === "country")) {
    return withCountryLink
  }

  return [
    ...withCountryLink,
    {
      name: "country",
      title: "Страна",
      label: "Страна",
      type: "link_to_item",
      dict: [],
      module_link_name: "countries",
    },
  ]
}

/**
 * Normalize catalog module item from API response
 * Transforms backend item structure to frontend model
 *
 * @param {Object} item - Raw item data from API
 * @param {string} item.id - Item ID (string from PHP mysqli)
 * @param {string} [item.name] - Item name
 * @param {number} [item.active] - Active status (0/1)
 * @param {number} [item.order_num] - Display order
 * @param {string} [item.created_at] - Creation timestamp
 * @param {string} [item.updated_at] - Update timestamp
 * @returns {Object} Normalized item object
 * @returns {number} returns.id - Item ID (converted to number)
 * @returns {string} [returns.name] - Item name
 * @returns {number} [returns.active] - Active status
 * @returns {number} [returns.orderNum] - Display order
 * @returns {string} [returns.createdAt] - Creation timestamp
 * @returns {string} [returns.updatedAt] - Update timestamp
 */
export function normalizeModuleItem(item) {
  if (!item || typeof item !== "object") {
    return null
  }

  return {
    id: item.id != null ? Number(item.id) : null,
    name: item.name ?? null,
    display_country: item.display_country ?? null,
    active: item.active ?? null,
    orderNum: item.order_num != null ? Number(item.order_num) : null,
    createdAt: item.created_at ?? null,
    updatedAt: item.updated_at ?? null,
  }
}

/**
 * Normalize module items response
 * Transforms backend items array to frontend model structure
 *
 * @param {Array} items - Raw items array
 * @returns {Array} Normalized items array
 */
export function normalizeModuleItems(items) {
  if (!Array.isArray(items)) {
    return []
  }

  return items.map(normalizeModuleItem).filter(Boolean)
}

const escapeRegex = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const stripLeadingArtistPrefix = (title, artist) => {
  const normalizedTitle = String(title || "").trim()
  const normalizedArtist = String(artist || "").trim()

  if (!normalizedTitle || !normalizedArtist) return normalizedTitle

  return normalizedTitle
    .replace(
      new RegExp(`^${escapeRegex(normalizedArtist)}\\s*[-—]\\s*`, "i"),
      ""
    )
    .trim()
}

/**
 * Normalize LP/CD row album display text for the title column.
 *
 * @precondition item is a raw module row object or a falsy/non-object value.
 * @postcondition Returns album/title text only; never substitutes artist text when album text is missing.
 * @postcondition Brace-encoded raw titles are treated as unreadable and parsed from the composite row title.
 * @invariant Artist prefixes are removed with primary artist first, so `display_artist` may still contain extra artists for composite search rendering.
 * @mutates none
 */
export function normalizeLpAlbumDisplayTitle(item) {
  if (!item || typeof item !== "object") return ""

  const rawTitle = String(item?.m_item_title || "").trim()
  const isBraceEncoded = /^\{[0-9,]*\}$/.test(rawTitle)
  const sourceTitle = isBraceEncoded
    ? String(item?.title || "").trim()
    : rawTitle

  const wasParsedFromComposite = isBraceEncoded || !rawTitle
  const normalizedSourceTitle = String(sourceTitle || "")
    .replace(/^(LP|CD)\s*:\s*/i, "")
    .replace(/\s*\(\d+\)\s*$/, "")
    .trim()
  let albumTitle = normalizedSourceTitle

  const primaryArtist = String(item?.artist_name || item?.artist || "").trim()
  const albumTitleWithoutPrimaryArtist = stripLeadingArtistPrefix(
    albumTitle,
    primaryArtist
  )
  let didStripArtistPrefix = albumTitleWithoutPrimaryArtist !== albumTitle
  albumTitle = albumTitleWithoutPrimaryArtist

  if (!primaryArtist) {
    const albumTitleWithoutDisplayArtist = stripLeadingArtistPrefix(
      albumTitle,
      item?.display_artist
    )
    didStripArtistPrefix = albumTitleWithoutDisplayArtist !== albumTitle
    albumTitle = albumTitleWithoutDisplayArtist
  }

  const artistValues = [primaryArtist, item?.display_artist, item?.artist]
    .map((value) =>
      String(value || "")
        .trim()
        .toLowerCase()
    )
    .filter(Boolean)

  if (
    wasParsedFromComposite &&
    !didStripArtistPrefix &&
    artistValues.includes(albumTitle.toLowerCase())
  ) {
    return ""
  }

  return albumTitle
}

/**
 * Normalize legacy LP/CD row display fields for module items list consumers.
 *
 * @precondition item is a raw legacy modules/items row object or non-object.
 * @postcondition Returns original row plus explicit `albumDisplayTitle`, `artistDisplayTitle`, and `searchDisplayTitle` fields.
 * @postcondition `albumDisplayTitle` is album-only text and never falls back to artist text.
 * @postcondition `searchDisplayTitle` contains artist+album composite only when both parts are present.
 * @mutates none
 */
export function normalizeLpCdListRowDisplayFields(item) {
  if (!item || typeof item !== "object") {
    return item
  }

  const albumDisplayTitle = normalizeLpAlbumDisplayTitle(item)
  const artistDisplayTitle = String(
    item?.display_artist || item?.artist_name || item?.artist || ""
  ).trim()
  const searchDisplayTitle =
    artistDisplayTitle && albumDisplayTitle
      ? `${artistDisplayTitle} — ${albumDisplayTitle}`
      : albumDisplayTitle
  const ownsItemLabel = Object.prototype.hasOwnProperty.call(
    item,
    "m_item_label"
  )
  const labelDisplayName = ownsItemLabel
    ? typeof item?.label_name === "string"
      ? item.label_name.trim()
      : ""
    : String(item?.label_name || "").trim()

  return {
    ...item,
    albumDisplayTitle,
    artistDisplayTitle,
    searchDisplayTitle,
    labelDisplayName,
  }
}

const LP_COPY_DATE_FIELDS = ["date_created", "date_sold"]
const LP_ZERO_DATE_SENTINEL = "0000-00-00 00:00:00"

/**
 * Normalize blank and legacy-zero LP-copy date fields at the API boundary.
 *
 * @precondition copies is an LP-copy array or a non-array value.
 * @postcondition Blank and legacy-zero date fields are null; canonical values and non-date fields are unchanged.
 * @mutates none
 */
export function normalizeLpCopyDates(copies) {
  if (!Array.isArray(copies)) return copies

  return copies.map((copy) => {
    if (!copy || typeof copy !== "object") return copy

    const normalizedCopy = { ...copy }
    LP_COPY_DATE_FIELDS.forEach((fieldName) => {
      if (!Object.prototype.hasOwnProperty.call(normalizedCopy, fieldName)) {
        return
      }

      const value = normalizedCopy[fieldName]
      if (
        value == null ||
        (typeof value === "string" &&
          (value.trim() === "" || value === LP_ZERO_DATE_SENTINEL))
      ) {
        normalizedCopy[fieldName] = null
      }
    })

    return normalizedCopy
  })
}

/**
 * Normalize LP-copy dates inside the legacy save payload at the API boundary.
 *
 * @precondition itemData is the mutable item payload; lpCopies may be an array or JSON string.
 * @postcondition Returns a new payload with LP copy dates serialized canonically; an explicit inSale command owns availability and removes the stale sold mirror. Rows without an explicit inSale command retain their existing fields.
 * @mutates none
 */
export function normalizeModuleItemForSavePayload(itemData) {
  if (
    !itemData ||
    typeof itemData !== "object" ||
    !Object.prototype.hasOwnProperty.call(itemData, "lpCopies")
  ) {
    return itemData
  }

  let copies = itemData.lpCopies
  if (typeof copies === "string") {
    try {
      copies = JSON.parse(copies)
    } catch {
      return itemData
    }
  }

  if (!Array.isArray(copies)) return itemData

  const normalizedCopies = normalizeLpCopyDates(copies).map((copy) => {
    if (!copy || typeof copy !== "object") return copy

    const hasExplicitInSale =
      Object.prototype.hasOwnProperty.call(copy, "inSale") &&
      copy.inSale != null &&
      (typeof copy.inSale !== "string" || copy.inSale.trim() !== "")

    if (
      !hasExplicitInSale ||
      !Object.prototype.hasOwnProperty.call(copy, "sold")
    ) {
      return copy
    }

    const normalizedCopy = { ...copy }
    delete normalizedCopy.sold
    return normalizedCopy
  })

  return {
    ...itemData,
    lpCopies: JSON.stringify(normalizedCopies),
  }
}

/**
 * Normalize legacy LP/CD edit payload title and copy dates before ModuleEdit hydration consumes it.
 *
 * @precondition payload is the raw `getModuleItemForEdit` response shape or any non-object value.
 * @precondition moduleId identifies the edited module; only LP/CD modules (115/112) are normalized.
 * @postcondition Returns payload with `itemInfo.common.m_item_title` normalized to album-only text and LP-copy dates canonicalized for LP/CD modules.
 * @postcondition When normalized title equals artist-only text or cannot produce album text, `m_item_title` becomes empty string.
 * @postcondition Non-LP/CD modules and malformed payloads are returned unchanged.
 * @mutates none
 */
export function normalizeModuleItemForEditPayload(payload, { moduleId } = {}) {
  if (!payload || typeof payload !== "object") return payload

  const numericModuleId = Number(moduleId)
  const isLpCdModule = numericModuleId === 112 || numericModuleId === 115
  if (!isLpCdModule) return payload

  const itemInfo = payload.itemInfo
  const common = itemInfo?.common
  if (
    !itemInfo ||
    typeof itemInfo !== "object" ||
    !common ||
    typeof common !== "object"
  ) {
    return payload
  }

  const normalizedTitle = normalizeLpAlbumDisplayTitle({
    ...itemInfo,
    m_item_title: common.m_item_title,
  })
  const normalizedCopies = Array.isArray(itemInfo.lp_copies)
    ? { lp_copies: normalizeLpCopyDates(itemInfo.lp_copies) }
    : {}

  return {
    ...payload,
    itemInfo: {
      ...itemInfo,
      ...normalizedCopies,
      common: {
        ...common,
        m_item_title: normalizedTitle,
      },
    },
  }
}

/**
 * Normalize delete response
 *
 * @param {Object} response - Raw delete response
 * @returns {{result: string, message: string|null}} Normalized delete response
 */
export function normalizeDeleteResponse(response) {
  if (!response || typeof response !== "object") {
    return {
      result: "unknown",
      message: null,
    }
  }

  return {
    result: response.result ?? "unknown",
    message: response.message ?? null,
  }
}

/**
 * Normalize toggle response
 *
 * @param {Object} response - Raw toggle response
 * @returns {{result: string, message: string|null}} Normalized toggle response
 */
export function normalizeToggleResponse(response) {
  return normalizeDeleteResponse(response)
}

/**
 * Normalize duplicate response
 *
 * @param {Object} response - Raw duplicate response
 * @returns {{result: string, moduleId: number|null, message: string|null}} Normalized duplicate response
 */
export function normalizeDuplicateResponse(response) {
  if (!response || typeof response !== "object") {
    return {
      result: "unknown",
      moduleId: null,
      message: null,
    }
  }

  return {
    result: response.result ?? "unknown",
    moduleId:
      response.module_id != null
        ? Number(response.module_id)
        : response.moduleId != null
          ? Number(response.moduleId)
          : null,
    message: response.message ?? null,
  }
}

/**
 * Normalize export response
 *
 * @param {Object} response - Raw export response
 * @returns {{result: string, data: unknown|null, message: string|null}} Normalized export response
 */
export function normalizeExportResponse(response) {
  if (!response || typeof response !== "object") {
    return {
      result: "unknown",
      data: null,
      message: null,
    }
  }

  return {
    result: response.result ?? "unknown",
    data: response.data ?? null,
    message: response.message ?? null,
  }
}

/**
 * Normalize filled boxes response
 *
 * @param {Array<number|string>} boxes - Raw boxes list from backend
 * @returns {Array<number>} Sorted unique numeric box numbers
 */
export function normalizeFilledBoxes(boxes) {
  if (!Array.isArray(boxes)) {
    return []
  }

  const normalized = boxes
    .map((box) => Number(box))
    .filter((box) => Number.isFinite(box) && box !== 0)
    .map((box) => Math.trunc(box))

  return [...new Set(normalized)].sort((a, b) => a - b)
}
